/**
 * Filter Logger Service
 * 
 * Comprehensive logging service for tracking all filter operations,
 * state changes, and user interactions in the filtering system.
 * 
 * Features:
 * - State change tracking with before/after snapshots
 * - User interaction logging
 * - Performance monitoring
 * - Filter analytics
 * - Debugging support
 */

import { Business } from '@/types/homepage';

// Types for logging
interface FilterState {
  searchQuery: string;
  isExplorationMode: boolean;
  activityFilters: { 
    showAnimalActivities: boolean; 
    showAdventureActivities: boolean; 
  };
  selectedAnimalTypes: string[];
  activeExperienceFilter: string | null;
  priceRange?: [number, number];
  duration?: [number, number];
  groupSize?: [number, number];
  amenities?: Record<string, boolean>;
}

interface FilterOperation {
  id: string;
  type: 'apply' | 'reset' | 'change' | 'interaction';
  timestamp: number;
  filterType: string;
  before: {
    state: Partial<FilterState>;
    businessCount: number;
    businesses: string[]; // business IDs
  };
  after: {
    state: Partial<FilterState>;
    businessCount: number;
    businesses: string[]; // business IDs
  };
  performance: {
    duration: number;
    cacheHit?: boolean;
  };
  userInteraction?: {
    component: string;
    action: string;
    value: any;
  };
}

interface FilterAnalytics {
  totalOperations: number;
  averageFilterTime: number;
  mostUsedFilters: Record<string, number>;
  filterEffectiveness: Record<string, { 
    averageReduction: number;
    usageCount: number;
  }>;
  cacheHitRate: number;
}

class FilterLoggerService {
  private operations: FilterOperation[] = [];
  private readonly MAX_OPERATIONS = 1000; // Keep last 1000 operations
  private enabled: boolean = true;
  
  constructor() {
    // Enable logging in development mode
    this.enabled = process.env.NODE_ENV === 'development';
  }

  /**
   * Log a filter operation with complete state tracking
   */
  logFilterOperation(params: {
    type: FilterOperation['type'];
    filterType: string;
    beforeState: Partial<FilterState>;
    afterState: Partial<FilterState>;
    beforeBusinesses: Business[];
    afterBusinesses: Business[];
    duration: number;
    cacheHit?: boolean;
    userInteraction?: FilterOperation['userInteraction'];
  }): void {
    if (!this.enabled) return;

    const operation: FilterOperation = {
      id: this.generateOperationId(),
      type: params.type,
      timestamp: Date.now(),
      filterType: params.filterType,
      before: {
        state: params.beforeState,
        businessCount: params.beforeBusinesses.length,
        businesses: params.beforeBusinesses.map(b => b._id)
      },
      after: {
        state: params.afterState,
        businessCount: params.afterBusinesses.length,
        businesses: params.afterBusinesses.map(b => b._id)
      },
      performance: {
        duration: params.duration,
        cacheHit: params.cacheHit
      },
      userInteraction: params.userInteraction
    };

    this.operations.push(operation);
    
    // Keep only the most recent operations
    if (this.operations.length > this.MAX_OPERATIONS) {
      this.operations.shift();
    }

    // Log to console with detailed information
    this.logToConsole(operation);
  }

  /**
   * Log user interaction with filter components
   */
  logUserInteraction(params: {
    component: string;
    action: string;
    value: any;
    currentState: Partial<FilterState>;
    currentBusinesses: Business[];
  }): void {
    if (!this.enabled) return;

    const interactionLog = {
      timestamp: Date.now(),
      component: params.component,
      action: params.action,
      value: params.value,
      currentState: params.currentState,
      businessCount: params.currentBusinesses.length,
      context: this.getFilterContext(params.currentState)
    };

    console.log(`[FilterLogger][INTERACTION] ${params.component} - ${params.action}:`, interactionLog);
  }

  /**
   * Log filter state changes with detailed comparison
   */
  logStateChange(params: {
    changedProperty: string;
    beforeValue: any;
    afterValue: any;
    fullStateBefore: Partial<FilterState>;
    fullStateAfter: Partial<FilterState>;
    businessesBefore: Business[];
    businessesAfter: Business[];
  }): void {
    if (!this.enabled) return;

    const stateChangeLog = {
      timestamp: Date.now(),
      property: params.changedProperty,
      change: {
        from: params.beforeValue,
        to: params.afterValue
      },
      impact: {
        businessCount: {
          before: params.businessesBefore.length,
          after: params.businessesAfter.length,
          change: params.businessesAfter.length - params.businessesBefore.length
        },
        addedBusinesses: params.businessesAfter
          .filter(b => !params.businessesBefore.find(bb => bb._id === b._id))
          .map(b => ({ id: b._id, name: b.name })),
        removedBusinesses: params.businessesBefore
          .filter(b => !params.businessesAfter.find(bb => bb._id === b._id))
          .map(b => ({ id: b._id, name: b.name }))
      },
      fullState: {
        before: params.fullStateBefore,
        after: params.fullStateAfter
      }
    };

    console.log(`[FilterLogger][STATE_CHANGE] ${params.changedProperty}:`, stateChangeLog);
  }

  /**
   * Log filter performance metrics
   */
  logPerformance(params: {
    filterType: string;
    duration: number;
    businessesProcessed: number;
    resultCount: number;
    cacheHit?: boolean;
    filtersApplied: string[];
  }): void {
    if (!this.enabled) return;

    const performanceLog = {
      timestamp: Date.now(),
      filterType: params.filterType,
      metrics: {
        duration: `${params.duration.toFixed(2)}ms`,
        businessesProcessed: params.businessesProcessed,
        resultCount: params.resultCount,
        efficiency: `${((params.resultCount / params.businessesProcessed) * 100).toFixed(1)}%`,
        cacheHit: params.cacheHit,
        filtersApplied: params.filtersApplied
      },
      performance: this.categorizePerformance(params.duration),
      warning: params.duration > 10 ? 'Slow filter detected' : null
    };

    console.log(`[FilterLogger][PERFORMANCE] ${params.filterType}:`, performanceLog);

    // Warn about slow filters
    if (params.duration > 50) {
      console.warn(`[FilterLogger][SLOW_FILTER] ${params.filterType} took ${params.duration.toFixed(2)}ms`, {
        businessesProcessed: params.businessesProcessed,
        filtersApplied: params.filtersApplied
      });
    }
  }

  /**
   * Log complete filter state snapshot
   */
  logFilterSnapshot(params: {
    reason: string;
    state: FilterState;
    businesses: Business[];
    additionalContext?: any;
  }): void {
    if (!this.enabled) return;

    const snapshotLog = {
      timestamp: Date.now(),
      reason: params.reason,
      snapshot: {
        filterState: params.state,
        businessCount: params.businesses.length,
        businesses: params.businesses.map(b => ({
          id: b._id,
          name: b.name,
          hasAnimalActivities: b.hasAnimalActivities,
          hasAdventureActivities: b.hasAdventureActivities
        })),
        activeFilters: this.getActiveFilters(params.state),
        filterComplexity: this.calculateFilterComplexity(params.state)
      },
      context: params.additionalContext
    };

    console.log(`[FilterLogger][SNAPSHOT] ${params.reason}:`, snapshotLog);
  }

  /**
   * Get filter analytics
   */
  getAnalytics(): FilterAnalytics {
    const analytics: FilterAnalytics = {
      totalOperations: this.operations.length,
      averageFilterTime: this.calculateAverageFilterTime(),
      mostUsedFilters: this.calculateMostUsedFilters(),
      filterEffectiveness: this.calculateFilterEffectiveness(),
      cacheHitRate: this.calculateCacheHitRate()
    };

    return analytics;
  }

  /**
   * Get recent operations for debugging
   */
  getRecentOperations(count: number = 10): FilterOperation[] {
    return this.operations.slice(-count);
  }

  /**
   * Clear all logged operations
   */
  clearLogs(): void {
    this.operations = [];
    console.log('[FilterLogger] Logs cleared');
  }

  /**
   * Enable or disable logging
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    console.log(`[FilterLogger] Logging ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Private helper methods
  private generateOperationId(): string {
    return `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private logToConsole(operation: FilterOperation): void {
    const logLevel = this.getLogLevel(operation);
    const message = `[FilterLogger][${operation.type.toUpperCase()}] ${operation.filterType}`;
    
    const logData = {
      operation: {
        id: operation.id,
        type: operation.type,
        filterType: operation.filterType
      },
      impact: {
        businessCount: {
          before: operation.before.businessCount,
          after: operation.after.businessCount,
          change: operation.after.businessCount - operation.before.businessCount
        }
      },
      state: {
        before: operation.before.state,
        after: operation.after.state
      },
      performance: {
        duration: `${operation.performance.duration.toFixed(2)}ms`,
        cacheHit: operation.performance.cacheHit
      },
      userInteraction: operation.userInteraction
    };

    if (logLevel === 'warn') {
      console.warn(message, logData);
    } else {
      console.log(message, logData);
    }
  }

  private getLogLevel(operation: FilterOperation): 'log' | 'warn' {
    // Warn for slow operations or operations that filter out all results
    if (operation.performance.duration > 10 || operation.after.businessCount === 0) {
      return 'warn';
    }
    return 'log';
  }

  private getFilterContext(state: Partial<FilterState>): string {
    const activeFilters = this.getActiveFilters(state);
    return `${activeFilters.length} active filter(s): ${activeFilters.join(', ')}`;
  }

  private getActiveFilters(state: Partial<FilterState>): string[] {
    const active: string[] = [];
    
    if (state.searchQuery?.trim()) active.push('search');
    if (state.selectedAnimalTypes?.length) active.push('animalTypes');
    if (state.activeExperienceFilter) active.push('experience');
    if (state.activityFilters) {
      const { showAnimalActivities, showAdventureActivities } = state.activityFilters;
      if (!showAnimalActivities || !showAdventureActivities) active.push('activityType');
    }
    
    return active;
  }

  private calculateFilterComplexity(state: FilterState): 'simple' | 'moderate' | 'complex' {
    const activeCount = this.getActiveFilters(state).length;
    if (activeCount <= 1) return 'simple';
    if (activeCount <= 3) return 'moderate';
    return 'complex';
  }

  private categorizePerformance(duration: number): 'fast' | 'moderate' | 'slow' {
    if (duration < 5) return 'fast';
    if (duration < 20) return 'moderate';
    return 'slow';
  }

  private calculateAverageFilterTime(): number {
    if (this.operations.length === 0) return 0;
    const total = this.operations.reduce((sum, op) => sum + op.performance.duration, 0);
    return total / this.operations.length;
  }

  private calculateMostUsedFilters(): Record<string, number> {
    const usage: Record<string, number> = {};
    this.operations.forEach(op => {
      usage[op.filterType] = (usage[op.filterType] || 0) + 1;
    });
    return usage;
  }

  private calculateFilterEffectiveness(): Record<string, { averageReduction: number; usageCount: number }> {
    const effectiveness: Record<string, { averageReduction: number; usageCount: number }> = {};
    
    this.operations.forEach(op => {
      if (!effectiveness[op.filterType]) {
        effectiveness[op.filterType] = { averageReduction: 0, usageCount: 0 };
      }
      
      const reduction = op.before.businessCount - op.after.businessCount;
      const reductionPercent = (reduction / op.before.businessCount) * 100;
      
      effectiveness[op.filterType].averageReduction = 
        (effectiveness[op.filterType].averageReduction * effectiveness[op.filterType].usageCount + reductionPercent) /
        (effectiveness[op.filterType].usageCount + 1);
      
      effectiveness[op.filterType].usageCount++;
    });
    
    return effectiveness;
  }

  private calculateCacheHitRate(): number {
    const cacheOperations = this.operations.filter(op => op.performance.cacheHit !== undefined);
    if (cacheOperations.length === 0) return 0;
    
    const hits = cacheOperations.filter(op => op.performance.cacheHit).length;
    return (hits / cacheOperations.length) * 100;
  }
}

// Export singleton instance
export const filterLogger = new FilterLoggerService();

// Export types for use in other components
export type { FilterState, FilterOperation, FilterAnalytics }; 